
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Calculator, 
  TrendingUp, 
  TrendingDown, 
  MapPin, 
  Users, 
  Wrench,
  PieChart,
  FileSpreadsheet,
  AlertTriangle,
  Download,
  Upload,
  Search,
  Filter
} from "lucide-react";
import { PricingStandard, LabourRate, RegionalFactor, CostingModel } from "../../types";

// Import sub-components
import RateCalculator from "./pricing/RateCalculator";
import LaborDatabase from "./pricing/LaborDatabase";
import HistoricalAnalysis from "./pricing/HistoricalAnalysis";

const PricingStandardModule = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRegion, setSelectedRegion] = useState<string>("all");

  // Sample data
  const [pricingStandards, setPricingStandards] = useState<PricingStandard[]>([
    {
      id: "PS-001",
      itemCode: "CON-001",
      itemName: "Concrete Grade 30",
      description: "Ready-mix concrete grade 30 for structural work",
      category: "Construction Materials",
      unit: "m³",
      materialCost: 12000,
      labourCost: 3500,
      overhead: 1500,
      baseProfit: 2000,
      allInRate: 19000,
      variance: {
        minRate: 16500,
        maxRate: 22000,
        averageRate: 19000,
        standardDeviation: 1200,
        confidenceLevel: 95
      },
      region: "Lagos",
      currency: "NGN",
      lastUpdated: "2024-01-15",
      historicalData: [],
      riskFactors: []
    }
  ]);

  const summaryCards = [
    { title: "Total Standards", value: pricingStandards.length, icon: Calculator, color: "text-blue-600" },
    { title: "Categories", value: 12, icon: PieChart, color: "text-green-600" },
    { title: "Regions Covered", value: 36, icon: MapPin, color: "text-purple-600" },
    { title: "Labor Rates", value: 150, icon: Users, color: "text-orange-600" }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Pricing Standard Module</h2>
          <p className="text-gray-600">
            Comprehensive pricing standards with variance analysis and regional factors
          </p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline">
            <Upload className="h-4 w-4 mr-2" />
            Import Standards
          </Button>
          <Button>
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {summaryCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
                <Icon className={`h-4 w-4 ${card.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{card.value}</div>
                <p className="text-xs text-muted-foreground">Active entries</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Historical Data is the main view; tabs removed */}
      <HistoricalAnalysis pricingStandards={pricingStandards} />
    </div>
  );
};

export default PricingStandardModule;

